// Lista sedi di un'azienda con mappa Leaflet e gestione CRUD
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, MapPin, Phone, Mail, Pencil, ChevronDown, ChevronUp, Trash2 } from 'lucide-react'
import { Button } from '../../../components/ui/Button'
import { Badge } from '../../../components/ui/Badge'
import { SiteMap, type SiteMapMarker } from './SiteMap'
import { SiteForm, type SiteData } from './SiteForm'
import { SiteContactsPanel } from './SiteContactsPanel'
import api from '../../../lib/api'

interface SiteFull extends SiteData {
  isActive?: boolean
}

interface SitesListProps {
  companyId: string
}

/**
 * Componente che gestisce la lista sedi con mappa, form e referenti.
 */
export function SitesList({ companyId }: SitesListProps) {
  const queryClient = useQueryClient()
  const [formOpen, setFormOpen] = useState(false)
  const [editingSite, setEditingSite] = useState<SiteData | null>(null)
  const [expandedSiteId, setExpandedSiteId] = useState<string | null>(null)

  const deleteSiteMutation = useMutation({
    mutationFn: async (siteId: string) => {
      await api.delete(`/sites/${siteId}`)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sites', 'company', companyId] })
    },
  })

  const handleDeleteSite = (site: SiteData) => {
    if (window.confirm(`Eliminare la sede "${site.name}"?`)) {
      deleteSiteMutation.mutate(site.id)
    }
  }

  const { data: sites = [], isLoading } = useQuery({
    queryKey: ['sites', 'company', companyId],
    queryFn: async () => {
      try {
        const res = await api.get<SiteFull[]>('/sites', { params: { companyId } })
        return res.data ?? []
      } catch {
        return [] as SiteFull[]
      }
    },
    enabled: Boolean(companyId),
  })

  const mapMarkers: SiteMapMarker[] = sites
    .filter((s) => s.latitude && s.longitude)
    .map((s) => ({
      id: s.id,
      name: s.name,
      address: s.address,
      city: s.city ?? undefined,
      latitude: s.latitude!,
      longitude: s.longitude!,
    }))

  const openAdd = () => {
    setEditingSite(null)
    setFormOpen(true)
  }

  const openEdit = (site: SiteData) => {
    setEditingSite(site)
    setFormOpen(true)
  }

  const toggleContacts = (siteId: string) => {
    setExpandedSiteId((prev) => (prev === siteId ? null : siteId))
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-sm text-[var(--fc-foreground-muted)]">
        Caricamento sedi...
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header azione */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-[var(--fc-foreground-muted)]">
          {sites.length} {sites.length === 1 ? 'sede' : 'sedi'} registrate
        </p>
        <Button
          size="sm"
          leftIcon={<Plus className="h-4 w-4" />}
          onClick={openAdd}
        >
          Nuova sede
        </Button>
      </div>

      {/* Mappa Leaflet */}
      {mapMarkers.length > 0 && (
        <SiteMap sites={mapMarkers} height={280} />
      )}

      {/* Lista sedi */}
      {sites.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 text-center">
          <MapPin className="h-10 w-10 text-[var(--fc-foreground-muted)] mb-2" aria-hidden="true" />
          <p className="text-sm text-[var(--fc-foreground-muted)]">Nessuna sede registrata</p>
          <Button size="sm" variant="secondary" className="mt-3" onClick={openAdd}>
            Aggiungi la prima sede
          </Button>
        </div>
      ) : (
        <ul className="space-y-2">
          {sites.map((site) => (
            <li
              key={site.id}
              className="rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] overflow-hidden"
            >
              {/* Header sede */}
              <div className="flex items-start justify-between p-3">
                <div className="space-y-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-[var(--fc-foreground)]">{site.name}</p>
                    {site.latitude && site.longitude && (
                      <Badge variant="default">
                        <MapPin className="h-2.5 w-2.5 mr-0.5" />
                        Geocodificata
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-[var(--fc-foreground-muted)] truncate">
                    {[site.address, site.city, site.postalCode].filter(Boolean).join(', ')}
                  </p>
                  <div className="flex flex-wrap gap-3 text-xs text-[var(--fc-foreground-muted)]">
                    {site.contactName && (
                      <span className="flex items-center gap-1">
                        {site.contactName}
                      </span>
                    )}
                    {site.contactPhone && (
                      <span className="flex items-center gap-1">
                        <Phone className="h-3 w-3" />
                        {site.contactPhone}
                      </span>
                    )}
                    {site.contactEmail && (
                      <span className="flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        {site.contactEmail}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0 ml-2">
                  <Button
                    size="icon-sm"
                    variant="ghost"
                    onClick={() => openEdit(site)}
                    aria-label={`Modifica sede ${site.name}`}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    size="icon-sm"
                    variant="ghost"
                    onClick={() => handleDeleteSite(site)}
                    aria-label={`Elimina sede ${site.name}`}
                    disabled={deleteSiteMutation.isPending}
                  >
                    <Trash2 className="h-3.5 w-3.5 text-[var(--fc-danger)]" />
                  </Button>
                  <Button
                    size="icon-sm"
                    variant="ghost"
                    onClick={() => toggleContacts(site.id)}
                    aria-label="Mostra referenti"
                    aria-expanded={expandedSiteId === site.id}
                  >
                    {expandedSiteId === site.id ? (
                      <ChevronUp className="h-3.5 w-3.5" />
                    ) : (
                      <ChevronDown className="h-3.5 w-3.5" />
                    )}
                  </Button>
                </div>
              </div>

              {/* Note tecniche (se presenti) */}
              {site.technicalNotes && (
                <div className="border-t border-[var(--fc-border)] px-3 py-2 bg-[var(--fc-surface-overlay)]">
                  <p className="text-xs font-medium text-[var(--fc-foreground-muted)] mb-0.5">Note tecniche</p>
                  <p className="text-xs text-[var(--fc-foreground)] whitespace-pre-line">{site.technicalNotes}</p>
                </div>
              )}

              {/* Referenti espandibili */}
              {expandedSiteId === site.id && (
                <div className="border-t border-[var(--fc-border)] px-3 py-3 bg-[var(--fc-surface-overlay)]">
                  <SiteContactsPanel siteId={site.id} siteName={site.name} />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Form modale creazione/modifica */}
      <SiteForm
        companyId={companyId}
        site={editingSite}
        open={formOpen}
        onClose={() => setFormOpen(false)}
      />
    </div>
  )
}
